import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { animate } from "framer-motion";
import Image from "next/legacy/image";
import Link from "next/link";
import { createRef, useEffect, useRef } from "react";
import DraggableScrollArea from "./DraggableScrollArea";

const HorizontalCreatorSelector = ({ items }) => {
  // Identify if any of the items are active (isActive is true)
  const activeCreator = items.find((item) => item.isActive);

  const itemRefs = useRef(items.map(() => createRef()));

  const containerRef = useRef(null); // needs to be passed to the draggable scroll area component

  useEffect(() => {
    // Find the active item
    const activeIndex = items.findIndex((item) => item.isActive);

    if (activeIndex !== -1) {
      const activeRef = itemRefs.current[activeIndex];
      const activeElement = activeRef.current;

      if (activeElement && containerRef.current) {
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = activeElement.getBoundingClientRect();

        // Identify the center of the active element
        const elementCenter =
          elementRect.left + elementRect.width / 2 - containerRect.left;

        // Calculate the center of the container
        const containerCenter = containerRect.width / 2;

        // Calculate the amount to scroll
        const toScroll = elementCenter - containerCenter;

        // Animate scroll using Framer Motion's animate function
        animate(container.scrollLeft, container.scrollLeft + toScroll, {
          type: "spring",
          stiffness: 150,
          damping: 20,
          onUpdate: (value) => (container.scrollLeft = value),
        });
      }
    }
  }, [items]);

  return (
    (<div
      className={clsx(
        "HorizontalCreatorSelector",
        "relative w-full bg-zinc-100 lg:hidden"
      )}
    >
      {!!activeCreator && (
        <Link
          href="/chefs"
          className={clsx(
            "HorizontalCreatorSelector__backButton",
            "absolute left-6 top-4 z-10 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-white transition ease-out"
          )}>

          <ArrowLeftIcon className={clsx("h-4 w-4 stroke-[2]")} />

        </Link>
      )}
      <DraggableScrollArea
        ref={containerRef}
        className={clsx("HorizontalCreatorSelector__scrollArea")}
      >
        <div className={clsx("flex space-x-2.5 px-22.5 py-4", {})}>
          {items.map((item, index) => {
            return (
              (<Link
                key={item.key}
                href={item.href}
                className={clsx(
                  "relative flex h-14 w-14 select-none items-center justify-center overflow-hidden rounded-full transition ease-out",
                  {
                    "opacity-50 hover:opacity-100": !item.isActive,
                    "opacity-100": item.isActive,
                  }
                )}
                draggable={false}
                ref={itemRefs.current[index]}>

                <Image
                  src={item.imageSrc}
                  alt={item.label}
                  layout="fill"
                  objectFit="cover"
                  draggable={false}
                />

              </Link>)
            );
          })}
        </div>
      </DraggableScrollArea>
      <div
        className={clsx(
          "pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-l from-transparent via-zinc-100 to-zinc-100"
        )}
      ></div>
      <div
        className={clsx(
          "pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-r from-transparent to-zinc-100"
        )}
      ></div>
    </div>)
  );
};

export default HorizontalCreatorSelector;
